export interface BuiltinCatalogItem {
  sku: string;
  name: string;
  businessVertical: string;
  category: string;
  subcategory: string;
  baseUnit: string;
  salePrice: number;
  minStockQty: number;
  trackExpiry: boolean;
}

type VariantGroup =
  | "packaged" | "beverage" | "liquid" | "wire" | "plumbing"
  | "electrical" | "dimensions" | "mechanical" | "bearing"
  | "electronics" | "apparel" | "footwear" | "foodservice"
  | "agriculture" | "auto" | "stationery";

type FamilySeed = readonly [
  code: string,
  category: string,
  subcategory: string,
  baseName: string,
  baseUnit: string,
  variantGroup: VariantGroup,
  trackExpiry?: boolean,
];

function cross(left: readonly string[], right: readonly string[]): string[] {
  return left.flatMap((a) => right.map((b) => `${a} · ${b}`));
}

const variants: Record<VariantGroup, string[]> = {
  packaged: cross(
    ["50 g","100 g","150 g","200 g","250 g","300 g","400 g","500 g","750 g","1 kg"],
    ["gói","hộp","túi","lốc","thùng"],
  ),
  beverage: cross(
    ["180 ml","250 ml","320 ml","330 ml","355 ml","500 ml","750 ml","1 L","1.5 L","2 L"],
    ["chai","lon","hộp","lốc 6","thùng 24"],
  ),
  liquid: cross(
    ["100 ml","200 ml","250 ml","500 ml","750 ml","1 L","1.5 L","2 L","3 L","5 L"],
    ["chai","can","túi","bình","thùng"],
  ),
  wire: cross(
    ["0.5 mm²","0.75 mm²","1.0 mm²","1.5 mm²","2.5 mm²","4 mm²","6 mm²","10 mm²","16 mm²","25 mm²"],
    ["1 m","5 m","10 m","50 m","100 m"],
  ),
  plumbing: cross(
    ["D21","D27","D34","D42","D49","D60","D76","D90","D110","D140"],
    ["PVC","uPVC","PPR","HDPE","INOX"],
  ),
  electrical: cross(
    ["5A","6A","10A","16A","20A","25A","32A","40A","50A","63A"],
    ["1 cực","2 cực","3 cực","âm tường","lắp nổi"],
  ),
  dimensions: cross(
    ["10 mm","12 mm","16 mm","20 mm","25 mm","30 mm","40 mm","50 mm","75 mm","100 mm"],
    ["Tiêu chuẩn","Cấp A","Cấp B","Nặng","Nhẹ"],
  ),
  mechanical: cross(
    ["M4","M5","M6","M8","M10","M12","M14","M16","M20","M24"],
    ["Thép","Inox 201","Inox 304","Mạ kẽm","Cường lực"],
  ),
  bearing: cross(
    ["6000","6001","6002","6003","6004","6200","6201","6202","6203","6204"],
    ["Hở","ZZ","2RS","C3","C4"],
  ),
  electronics: cross(
    ["5W","10W","18W","20W","30W","45W","65W","100W","128GB","256GB"],
    ["USB-A","USB-C","Lightning","Không dây","Chuẩn phổ thông"],
  ),
  apparel: cross(
    ["XS","S","M","L","XL","2XL","3XL","28","30","32"],
    ["Đen","Trắng","Xanh","Đỏ","Be"],
  ),
  footwear: cross(
    ["35","36","37","38","39","40","41","42","43","44"],
    ["Đen","Trắng","Nâu","Xám","Xanh"],
  ),
  foodservice: cross(
    ["50 g","100 g","200 g","250 g","500 g","750 g","1 kg","2 kg","5 kg","10 kg"],
    ["gói","hộp","túi","khay","thùng"],
  ),
  agriculture: cross(
    ["10 g","50 g","100 g","250 g","500 g","1 kg","2 kg","5 kg","10 kg","25 kg"],
    ["gói","chai","bao","can","thùng"],
  ),
  auto: cross(
    ["50 cc","90 cc","110 cc","125 cc","150 cc","175 cc","250 cc","1.0L","1.5L","2.0L"],
    ["Trước","Sau","Trái","Phải","Bộ"],
  ),
  stationery: cross(
    ["A7","A6","A5","A4","A3","B5","0.5 mm","0.7 mm","1.0 mm","Khổ chuẩn"],
    ["lẻ","hộp 10","hộp 20","bộ 50","thùng"],
  ),
};

const verticalSeeds: Array<{ code: string; name: string; families: readonly FamilySeed[] }> = [
  { code:"TG", name:"Tạp hóa & Bách hóa", families:[
    ["NGK","Đồ uống","Nước giải khát","Nước giải khát","chai","beverage",true],
    ["NUOC","Đồ uống","Nước uống","Nước uống đóng chai","chai","beverage",true],
    ["SUA","Sữa & dinh dưỡng","Sữa","Sữa đóng hộp","hộp","beverage",true],
    ["MI","Thực phẩm khô","Mì ăn liền","Mì ăn liền","gói","packaged",true],
    ["SNACK","Bánh kẹo","Snack","Bánh snack","gói","packaged",true],
    ["GIAVI","Gia vị","Gia vị nấu ăn","Gia vị nấu ăn","gói","packaged",true],
    ["DOHOP","Thực phẩm","Đồ hộp","Thực phẩm đóng hộp","hộp","packaged",true],
    ["CSCN","Chăm sóc cá nhân","Vệ sinh cá nhân","Sản phẩm chăm sóc cá nhân","chai","liquid"],
    ["VESINH","Hóa phẩm","Tẩy rửa","Sản phẩm vệ sinh gia dụng","chai","liquid"],
    ["GIADUNG","Đồ gia dụng","Gia dụng nhỏ","Đồ gia dụng tiêu dùng","cái","dimensions"],
  ]},
  { code:"DN", name:"Điện & Nước", families:[
    ["DAYDIEN","Thiết bị điện","Dây & cáp điện","Dây điện","m","wire"],
    ["CONGTAC","Thiết bị điện","Công tắc","Công tắc điện","cái","electrical"],
    ["OCAM","Thiết bị điện","Ổ cắm","Ổ cắm điện","cái","electrical"],
    ["CB","Thiết bị điện","Thiết bị đóng cắt","CB/Aptomat","cái","electrical"],
    ["DEN","Chiếu sáng","Đèn","Đèn chiếu sáng","cái","electronics"],
    ["ONGDIEN","Ống & phụ kiện","Ống luồn điện","Ống luồn dây điện","cây","plumbing"],
    ["ONGNUOC","Thiết bị nước","Ống nước","Ống cấp thoát nước","cây","plumbing"],
    ["PHUKIENONG","Thiết bị nước","Phụ kiện ống","Co/Tê/Nối ống","cái","plumbing"],
    ["VAN","Thiết bị nước","Van","Van nước","cái","plumbing"],
    ["BOM","Máy bơm","Bơm dân dụng","Máy bơm nước","cái","electrical"],
  ]},
  { code:"VLXD", name:"Vật liệu xây dựng", families:[
    ["XIMANG","Vật liệu thô","Xi măng","Xi măng xây dựng","bao","foodservice"],
    ["THEP","Kim loại xây dựng","Sắt thép","Thép xây dựng","cây","dimensions"],
    ["GACH","Gạch & đá","Gạch xây","Gạch xây dựng","viên","dimensions"],
    ["SON","Sơn & phủ","Sơn nước","Sơn nước","thùng","liquid"],
    ["CHONGTHAM","Sơn & phủ","Chống thấm","Vật liệu chống thấm","thùng","liquid"],
    ["GACHMEN","Gạch & đá","Gạch ốp lát","Gạch ốp lát","thùng","dimensions"],
    ["THACHSAN","Trần & vách","Thạch cao","Tấm thạch cao","tấm","dimensions"],
    ["TON","Mái & tôn","Tôn lợp","Tôn lợp mái","m","dimensions"],
    ["VIT","Phụ kiện","Vít & tắc kê","Vít/tắc kê xây dựng","cái","mechanical"],
    ["DUNGCU","Dụng cụ","Dụng cụ xây dựng","Dụng cụ thi công","cái","dimensions"],
  ]},
  { code:"CK", name:"Cơ khí & Công nghiệp", families:[
    ["BACDAN","Truyền động","Bạc đạn","Vòng bi/bạc đạn","cái","bearing"],
    ["BULONG","Liên kết","Bu lông","Bu lông","cái","mechanical"],
    ["DAIOC","Liên kết","Đai ốc","Đai ốc","cái","mechanical"],
    ["DAICAT","Gia công","Đá cắt","Đá cắt kim loại","cái","dimensions"],
    ["MUIKHOAN","Gia công","Mũi khoan","Mũi khoan","cái","dimensions"],
    ["QUEHAN","Hàn","Que hàn","Que hàn","kg","dimensions"],
    ["KHINEN","Khí nén","Đầu nối khí","Đầu nối khí nén","cái","plumbing"],
    ["DAYCUROA","Truyền động","Dây curoa","Dây curoa","sợi","dimensions"],
    ["DAUNHOT","Bôi trơn","Dầu công nghiệp","Dầu bôi trơn công nghiệp","chai","liquid"],
    ["DUNGCU","Dụng cụ","Dụng cụ cơ khí","Dụng cụ cơ khí cầm tay","cái","dimensions"],
  ]},
  { code:"DT", name:"Điện tử & Công nghệ", families:[
    ["SAC","Phụ kiện điện thoại","Sạc","Củ sạc","cái","electronics"],
    ["CAP","Phụ kiện điện thoại","Cáp","Cáp dữ liệu/sạc","sợi","electronics"],
    ["PIN","Nguồn di động","Pin dự phòng","Pin dự phòng","cái","electronics"],
    ["THENHO","Lưu trữ","Thẻ nhớ","Thẻ nhớ","cái","electronics"],
    ["ROUTER","Mạng","Router","Bộ phát Wi-Fi","cái","electronics"],
    ["BANPHIM","Máy tính","Bàn phím","Bàn phím","cái","electronics"],
    ["CHUOT","Máy tính","Chuột","Chuột máy tính","cái","electronics"],
    ["TAINGHE","Âm thanh","Tai nghe","Tai nghe","cái","electronics"],
    ["CAMERA","An ninh","Camera","Camera giám sát","cái","electronics"],
    ["SMARTHOME","Nhà thông minh","Công tắc thông minh","Thiết bị nhà thông minh","cái","electrical"],
  ]},
  { code:"TT", name:"Thời trang", families:[
    ["AOTHUN","Quần áo","Áo thun","Áo thun","cái","apparel"],
    ["AOSOMI","Quần áo","Áo sơ mi","Áo sơ mi","cái","apparel"],
    ["QUANTAY","Quần áo","Quần tây","Quần tây","cái","apparel"],
    ["QUANJEAN","Quần áo","Quần jean","Quần jean","cái","apparel"],
    ["VAY","Quần áo","Váy/đầm","Váy/đầm","cái","apparel"],
    ["AOKHOAC","Quần áo","Áo khoác","Áo khoác","cái","apparel"],
    ["GIAY","Giày dép","Giày","Giày","đôi","footwear"],
    ["DEP","Giày dép","Dép","Dép","đôi","footwear"],
    ["TUI","Phụ kiện","Túi","Túi thời trang","cái","apparel"],
    ["NON","Phụ kiện","Nón","Nón/mũ","cái","apparel"],
  ]},
  { code:"FNB", name:"Nhà hàng & F&B", families:[
    ["GAO","Nguyên liệu","Gạo","Gạo","kg","foodservice",true],
    ["THIT","Nguyên liệu","Thịt","Thịt sơ chế","kg","foodservice",true],
    ["HAISAN","Nguyên liệu","Hải sản","Hải sản","kg","foodservice",true],
    ["RAU","Nguyên liệu","Rau củ","Rau củ quả","kg","foodservice",true],
    ["SOT","Gia vị","Sốt","Sốt chế biến","chai","liquid",true],
    ["NGUYENLIEU","Đồ uống","Nguyên liệu pha chế","Nguyên liệu pha chế","gói","foodservice",true],
    ["DONGLANH","Thực phẩm","Đông lạnh","Thực phẩm đông lạnh","gói","foodservice",true],
    ["BAOBI","Bao bì","Dùng một lần","Bao bì dùng một lần","cái","stationery"],
    ["BANH","Làm bánh","Nguyên liệu bánh","Nguyên liệu làm bánh","gói","foodservice",true],
    ["VESINH","Vận hành","Vệ sinh bếp","Hóa phẩm vệ sinh bếp","chai","liquid"],
  ]},
  { code:"NN", name:"Nông nghiệp", families:[
    ["HATGIONG","Giống","Hạt giống","Hạt giống","gói","agriculture",true],
    ["PHANBON","Vật tư nông nghiệp","Phân bón","Phân bón","bao","agriculture"],
    ["BVTV","Vật tư nông nghiệp","Bảo vệ thực vật","Chế phẩm bảo vệ thực vật","chai","liquid"],
    ["TUOI","Tưới tiêu","Thiết bị tưới","Thiết bị tưới","cái","plumbing"],
    ["THUCAN","Chăn nuôi","Thức ăn","Thức ăn chăn nuôi","bao","agriculture",true],
    ["THUY","Chăn nuôi","Vật tư thú y","Vật tư thú y","hộp","agriculture",true],
    ["DUNGCU","Dụng cụ","Dụng cụ nông nghiệp","Dụng cụ nông nghiệp","cái","dimensions"],
    ["NHALUOI","Nhà màng","Vật tư nhà màng","Vật tư nhà màng","m","dimensions"],
    ["CHAU","Ươm trồng","Chậu & khay","Chậu/khay ươm","cái","dimensions"],
    ["BAOBI","Đóng gói","Bao bì nông sản","Bao bì nông sản","cái","stationery"],
  ]},
  { code:"PTX", name:"Phụ tùng xe", families:[
    ["NHOT","Dầu nhớt","Nhớt động cơ","Dầu nhớt động cơ","chai","liquid"],
    ["LOC","Động cơ","Lọc","Lọc gió/lọc nhớt","cái","auto"],
    ["BUGI","Động cơ","Bugi","Bugi","cái","auto"],
    ["PHANH","Phanh","Má phanh","Má phanh","bộ","auto"],
    ["DEN","Điện xe","Bóng đèn","Bóng đèn xe","cái","electronics"],
    ["LOP","Lốp","Lốp xe","Lốp xe","cái","auto"],
    ["ACQUY","Điện xe","Ắc quy","Ắc quy","cái","electronics"],
    ["NHONGXICH","Truyền động","Nhông xích","Bộ nhông xích","bộ","auto"],
    ["DAYCAP","Điều khiển","Dây cáp","Dây cáp xe","sợi","auto"],
    ["CHAMSOC","Chăm sóc xe","Vệ sinh","Sản phẩm chăm sóc xe","chai","liquid"],
  ]},
  { code:"VPP", name:"Văn phòng phẩm & Giáo dục", families:[
    ["VO","Giấy vở","Vở","Vở học sinh","quyển","stationery"],
    ["BUTBI","Bút viết","Bút bi","Bút bi","cây","stationery"],
    ["BUTCHI","Bút viết","Bút chì","Bút chì","cây","stationery"],
    ["GIAY","Giấy vở","Giấy in","Giấy in","ream","stationery"],
    ["BIA","Lưu trữ","Bìa hồ sơ","Bìa hồ sơ","cái","stationery"],
    ["BAMKIM","Dụng cụ bàn","Bấm kim","Bấm kim","cái","stationery"],
    ["BANGKEO","Dụng cụ bàn","Băng keo","Băng keo văn phòng","cuộn","stationery"],
    ["BUTLONG","Bút viết","Bút lông","Bút lông","cây","stationery"],
    ["MUCIN","Máy in","Mực in","Mực in","hộp","electronics"],
    ["DUNGCUHT","Học cụ","Dụng cụ học tập","Dụng cụ học tập","cái","stationery"],
  ]},
];

export const BUILTIN_CATALOG: BuiltinCatalogItem[] = verticalSeeds.flatMap((vertical) =>
  vertical.families.flatMap(([familyCode, category, subcategory, baseName, baseUnit, variantGroup, trackExpiry = false]) =>
    variants[variantGroup].map((variant, index) => ({
      sku: `BL-${vertical.code}-${familyCode}-${String(index + 1).padStart(3, "0")}`.toUpperCase(),
      name: `${baseName} · ${variant}`,
      businessVertical: vertical.name,
      category,
      subcategory,
      baseUnit,
      salePrice: 0,
      minStockQty: 0,
      trackExpiry,
    })),
  ),
);

if (BUILTIN_CATALOG.length !== 5000) {
  throw new Error(`Builtin catalog must contain exactly 5000 SKU templates, got ${BUILTIN_CATALOG.length}`);
}

export const BUILTIN_VERTICALS = verticalSeeds.map(({ code, name }) => ({ code, name }));

export function builtinCategories(vertical?: string): string[] {
  return [...new Set(
    BUILTIN_CATALOG
      .filter((item) => !vertical || item.businessVertical === vertical)
      .map((item) => item.category),
  )].sort((a, b) => a.localeCompare(b, "vi"));
}
